import type { UpdateChunkBboxRequest } from '../types';

/**
 * Unsaved highlight edits (OMA-592 / OMA-593).
 *
 * Moving, drawing and removing a highlight are all held here until the operator
 * presses Save, so the viewer can render the pending state, an accidental drag
 * can be undone, and leaving the page can warn. Kept as a plain immutable value
 * with pure operations so the whole undo/redo stack is just an array of these.
 */
export interface HighlightDraft {
  /** chunk_id → geometry the operator dragged it to. */
  moves: Record<string, UpdateChunkBboxRequest>;
  /** Boxes drawn on the page that have no chunk row yet. */
  additions: HighlightAddition[];
  /** Existing references the operator unlinked. */
  removals: HighlightRemoval[];
  /**
   * Pending per-document recolors, keyed by extracted-field `key`. `null` means
   * "drop the override and fall back to the template color". Held here so a
   * recolor is previewed, undoable and saved with the same bar as the geometry
   * edits — it is still stored client-side once saved.
   */
  colors: Record<string, string | null>;
}

export interface HighlightAddition {
  /** Client-side identity — a drawn box has no chunk_id until it is saved. */
  tempId: string;
  resultId: number;
  fieldKey: string;
  /** Page-scopes the field for a repeating template; null for one-record ones. */
  fieldPageNumber: number | null;
  /** Container-wide viewer page index the box was drawn on. */
  pageIndex: number;
  bbox: UpdateChunkBboxRequest;
}

export interface HighlightRemoval {
  resultId: number;
  fieldKey: string;
  fieldPageNumber: number | null;
  /** `document_chunks.id` — what the DELETE route binds. */
  chunkDbId: number;
  /** `document_chunks.chunk_id` — what `references` stores. */
  chunkId: string;
}

export const EMPTY_HIGHLIGHT_DRAFT: HighlightDraft = {
  moves: {},
  additions: [],
  removals: [],
  colors: {}
};

/**
 * Screen pixels a pointer may travel before a press counts as a drag rather
 * than a click. A click on a highlight box selects its field, and a mouse
 * always slips a pixel or two while clicking — without this slop that slip
 * recorded a bbox move and raised the unsaved-changes bar unprompted.
 */
export const DRAG_THRESHOLD_PX = 3;

export function exceedsDragThreshold(dx: number, dy: number): boolean {
  return Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX;
}

export function isHighlightDraftDirty(draft: HighlightDraft): boolean {
  return (
    Object.keys(draft.moves).length > 0 ||
    draft.additions.length > 0 ||
    draft.removals.length > 0 ||
    Object.keys(draft.colors).length > 0
  );
}

/** Total pending operations — drives the "N unsaved changes" counter. */
export function highlightDraftSize(draft: HighlightDraft): number {
  return (
    Object.keys(draft.moves).length +
    draft.additions.length +
    draft.removals.length +
    Object.keys(draft.colors).length
  );
}

export function moveHighlight(
  draft: HighlightDraft,
  chunkId: string,
  bbox: UpdateChunkBboxRequest
): HighlightDraft {
  return { ...draft, moves: { ...draft.moves, [chunkId]: bbox } };
}

/**
 * Queue a recolor of one field's highlights. `null` clears the override back to
 * the template color. Last write per field wins — an operator cycling through
 * swatches is one pending change, not one per swatch.
 */
export function recolorField(
  draft: HighlightDraft,
  fieldKey: string,
  color: string | null
): HighlightDraft {
  return { ...draft, colors: { ...draft.colors, [fieldKey]: color } };
}

export function addHighlight(
  draft: HighlightDraft,
  addition: HighlightAddition
): HighlightDraft {
  return { ...draft, additions: [...draft.additions, addition] };
}

/**
 * Drop a drawn-but-unsaved box. Undoing a draw must not queue a DELETE for a
 * chunk the server has never heard of.
 */
export function discardAddition(
  draft: HighlightDraft,
  tempId: string
): HighlightDraft {
  return {
    ...draft,
    additions: draft.additions.filter((a) => a.tempId !== tempId)
  };
}

/**
 * Queue an existing reference for unlinking.
 *
 * Any pending move of the same chunk is dropped: saving both would PATCH a
 * geometry onto a chunk the very next request unlinks.
 */
export function removeHighlight(
  draft: HighlightDraft,
  removal: HighlightRemoval
): HighlightDraft {
  if (draft.removals.some((r) => r.chunkDbId === removal.chunkDbId)) {
    return draft;
  }

  const moves = { ...draft.moves };
  delete moves[removal.chunkId];

  return { ...draft, moves, removals: [...draft.removals, removal] };
}

/** chunk_ids the viewer must stop drawing because they are queued for removal. */
export function removedChunkIds(draft: HighlightDraft): Set<string> {
  return new Set(draft.removals.map((r) => r.chunkId));
}
